import type { ProseChange } from '@libs/types';

/** What the block syntax in front of a marked passage said it was. */
export type SegmentKind = 'paragraph' | 'heading' | 'list_item';

/**
 * One run of the original document: either plain text, or the passage a change
 * would remove or reword.
 */
export interface OriginalSegment {
  text: string;
  /** Set when this run is a change's `before` text. */
  change?: ProseChange;
  /**
   * Marked runs only. `text` keeps the block syntax it was written with so the
   * segments still reassemble into the source, and these say what to do with
   * it: strip `marker.length` characters, then render as `kind`. A marked run
   * is highlighted inline, and `## ` or `- ` left in the text would either
   * print literally or wrap the passage in a block the highlight cannot hug.
   */
  marker?: string;
  kind?: SegmentKind;
}

/**
 * Markdown syntax that carries no text. Stripped before matching so the AI's
 * plain-text `before` still lines up with a source passage that happens to be
 * bolded, quoted, or written as a heading.
 */
const NOISE = /[*_`#>~]/;

/** Everything a block-level prefix may be built from (`## `, `- `, `1. `, `> `). */
const BLOCK_PREFIX = /^[\s>*+\-#\d.)]*$/;

const HEADING_PREFIX = /^\s*#{1,6}\s*$/;
const LIST_PREFIX = /^\s*(?:[-*+]|\d+[.)])\s*$/;

/** The shortest `before` worth placing — below this, a match is a coincidence. */
const MIN_MATCH = 12;

interface Normalized {
  text: string;
  /** For each character in `text`, its index in the string it came from. */
  map: number[];
}

/**
 * Case-folded, whitespace-collapsed, markdown-stripped copy of `text`, with a
 * map back to the original offsets so a match can be re-expressed as a slice of
 * the untouched source.
 */
function normalize(text: string): Normalized {
  const out: string[] = [];
  const map: number[] = [];
  let pendingSpace = false;

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (NOISE.test(ch)) continue;
    if (/\s/.test(ch)) {
      pendingSpace = out.length > 0;
      continue;
    }
    if (pendingSpace) {
      out.push(' ');
      map.push(i);
      pendingSpace = false;
    }
    out.push(ch.toLowerCase());
    map.push(i);
  }

  return { text: out.join(''), map };
}

/**
 * Widen a match back to the start of its line when only block syntax sits in
 * front of it.
 *
 * A change's `before` is plain text, so a heading matches as "Retention" and
 * leaves the `## ` behind in the preceding segment — which would render as an
 * empty heading followed by an un-headed paragraph. Swallowing the prefix keeps
 * the marked passage the block it was.
 */
function expandToLineStart(
  source: string,
  start: number,
): { start: number; marker: string; kind: SegmentKind } {
  const lineStart = source.lastIndexOf('\n', start - 1) + 1;
  const marker = source.slice(lineStart, start);
  if (!BLOCK_PREFIX.test(marker)) return { start, marker: '', kind: 'paragraph' };

  const kind: SegmentKind = HEADING_PREFIX.test(marker)
    ? 'heading'
    : LIST_PREFIX.test(marker)
      ? 'list_item'
      : 'paragraph';
  return { start: lineStart, marker, kind };
}

/**
 * Split the original document around the passages the AI proposed to change.
 *
 * The original pane renders `sourceText` — the pristine upload — and has no
 * block structure of its own to key off: `blockId` addresses the *generated*
 * document, which by definition no longer contains what was removed. So a
 * change is placed by finding its `before` text in the source. Matching is
 * tolerant (case, whitespace and markdown decoration are ignored) because the
 * AI hands back plain prose while the source is extracted markdown.
 *
 * A `before` that cannot be placed is simply not marked — the pane still
 * renders the document in full, which is the important part. Additions never
 * appear here: there was nothing in the original to mark.
 */
export function buildOriginalSegments(
  sourceText: string,
  changes: ProseChange[],
): OriginalSegment[] {
  const hay = normalize(sourceText);
  const hits: { start: number; end: number; change: ProseChange }[] = [];

  for (const change of changes) {
    const needle = normalize(change.before ?? '').text;
    if (needle.length < MIN_MATCH) continue;

    // First occurrence no other change has taken, so two changes quoting the
    // same boilerplate mark two different passages instead of fighting over one.
    let from = 0;
    for (;;) {
      const at = hay.text.indexOf(needle, from);
      if (at < 0) break;
      const end = at + needle.length;
      if (!hits.some((h) => at < h.end && end > h.start)) {
        hits.push({ start: at, end, change });
        break;
      }
      from = at + 1;
    }
  }

  hits.sort((a, b) => a.start - b.start);

  const segments: OriginalSegment[] = [];
  let cursor = 0;
  for (const hit of hits) {
    const expanded = expandToLineStart(sourceText, hay.map[hit.start]);
    // Clamped: expanding to the line start must not reach back into a passage
    // an earlier change already claimed.
    const start = Math.max(cursor, expanded.start);
    const marker = expanded.start >= cursor ? expanded.marker : '';
    const end = hay.map[hit.end - 1] + 1;
    if (start > cursor) segments.push({ text: sourceText.slice(cursor, start) });
    segments.push({
      text: sourceText.slice(start, end),
      change: hit.change,
      marker,
      kind: marker ? expanded.kind : 'paragraph',
    });
    cursor = end;
  }
  if (cursor < sourceText.length) segments.push({ text: sourceText.slice(cursor) });

  return segments;
}
